/**
 * Type definitions for Transactions domain
 * Provides type-safe constants and interfaces for transaction filtering
 */

import dayjs from 'dayjs';

// ============================================================================
// ENUMS & CONSTANTS (Eliminating magic values)
// ============================================================================

/**
 * Valid sort column names
 * Prevents typos and documents available columns
 */
export const SORT_COLUMNS = {
  PARENT_CONTRACT_NUMBER: 'ParentContractNumber',
  CARD_NUMBER: 'CardNumber',
  TRANSACTION_DATE: 'transactionDate',
  TRANSACTION_AMOUNT: 'transactionAmount',
  REGISTRATION_NUMBER: 'registrationNumber',
} as const;

export type SortColumn = (typeof SORT_COLUMNS)[keyof typeof SORT_COLUMNS];

/**
 * Valid modal types
 */
export const MODAL_TYPES = {
  CARD: 'card',
  ACCOUNT: 'account',
  NONE: undefined,
} as const;

export type ModalType = typeof MODAL_TYPES.CARD | typeof MODAL_TYPES.ACCOUNT | undefined;

/**
 * Valid account levels
 */
export const ACCOUNT_LEVELS = {
  CONTRACT: 'C',
  GRANDFATHER: 'GP',
  PARENT: 'P',
} as const;

export type AccountLevel = (typeof ACCOUNT_LEVELS)[keyof typeof ACCOUNT_LEVELS];

/**
 * Pagination defaults
 */
export const PAGINATION_DEFAULTS = {
  PAGE_NUMBER: 1,
  PAGE_SIZE: 10,
  DOWNLOAD_PAGE_SIZE: 300,
  DOWNLOAD_PAGE: 0,
} as const;

/**
 * Date filter defaults
 */
export const DATE_DEFAULTS = {
  START_DATE_OFFSET_MONTHS: 1,
} as const;

/**
 * Sort defaults
 */
export const SORT_DEFAULTS = {
  COLUMN: SORT_COLUMNS.PARENT_CONTRACT_NUMBER,
  DIRECTION: false, // false = ascending
} as const;

// ============================================================================
// CORE DOMAIN TYPES
// ============================================================================

export interface TransactionFilters {
  contractId: string;
  cardNo: string;
  vehicleRegNo: string;
  driverName: string;
  sourceNo: string;
  startDate: string | null;
  endDate: string | null;
}

export interface PaginationState {
  pageSize: number;
  pageNumber: number;
}

export interface SortState {
  sortColumn: SortColumn;
  sortDirection: boolean; // false = ascending, true = descending
}

export interface FilterFlags {
  isFilterClick: boolean;
  triggerFilter: boolean;
}

/**
 * Complete filter state combining all concerns
 */
export interface FilterValues extends TransactionFilters, PaginationState, SortState, FilterFlags {}

export interface FilterInitializationContext {
  readonly accountId: string;
  readonly cardNo: string;
  readonly modalType?: string;
}

/**
 * GTM Event types
 */
export const GTM_EVENTS = {
  // Pagination
  PAGE_NUMBER: 'Card_Portal_Transactions_Page_Number',

  // Filter - Standard mode
  FILTER_STANDARD: 'Card_Portal_Transactions_Filter',
  CLEAR_ALL_STANDARD: 'Card_Portal_Transactions_Clear_All',

  // Filter - Card modal
  FILTER_CARD: 'Card_Portal_Individual_Card_Detail_Transactions_Filter',
  CLEAR_ALL_CARD: 'Card_Portal_Individual_Card_Detail_Transactions_Clear_All',

  // Filter - Account modal
  FILTER_ACCOUNT: 'Card_Portal_Accounts_Transaction_Filter',

  // Download
  DOWNLOAD_STANDARD: 'Card_Portal_Transactions_Download',
  DOWNLOAD_ACCOUNT: 'Card_Portal_Accounts_Transaction_Download',
} as const;

export type GTMEventName = (typeof GTM_EVENTS)[keyof typeof GTM_EVENTS];

/**
 * App Insights event types
 */
export const APP_INSIGHTS_EVENTS = {
  CHANGE_PAGE: 'Change Page Number',
  SUBMIT_FILTER: 'Submit Filter Transactions',
  DOWNLOAD: 'Click Download',
} as const;

export type AppInsightsEventName = (typeof APP_INSIGHTS_EVENTS)[keyof typeof APP_INSIGHTS_EVENTS];

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

export function createInitialFilterValues(
  context: FilterInitializationContext,
  startDate: string,
  endDate: string
): FilterValues {
  const { accountId, cardNo, modalType } = context;

  return {
    contractId: modalType ? accountId : '',
    cardNo: modalType === MODAL_TYPES.CARD ? cardNo : '',
    vehicleRegNo: '',
    driverName: '',
    sourceNo: '',
    startDate,
    endDate,
    pageSize: PAGINATION_DEFAULTS.PAGE_SIZE,
    pageNumber: PAGINATION_DEFAULTS.PAGE_NUMBER,
    sortColumn: SORT_DEFAULTS.COLUMN,
    sortDirection: SORT_DEFAULTS.DIRECTION,
    isFilterClick: false,
    triggerFilter: false,
  };
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export function createDefaultDateRange(): DateRange {
  const DATE_FORMAT = 'DD/MM/YYYY';

  return {
    startDate: dayjs().subtract(DATE_DEFAULTS.START_DATE_OFFSET_MONTHS, 'month').format(DATE_FORMAT),
    endDate: dayjs().format(DATE_FORMAT),
  };
}
